/**
 * Phase 106 (Plans 106-08, 106-09, 106-10, 106-11, 106-12) — superadmin
 * fleet console API client.
 *
 * Endpoints:
 *   GET  /api/fleet/instances                          → { data: FleetListing } (Plan 106-07)
 *   POST /api/fleet/instances/:id/preview-modules       → { data: ModuleChangePreview } (Plan 106-09)
 *   POST /api/fleet/instances/:id/modules                → { data: SaveModulesResult } (Plan 106-10)
 *   GET  /api/fleet/superadmins                          → { data: SuperadminHolder[] } (Plan 106-11)
 *   POST /api/fleet/superadmins                           → { data: SuperadminMutationResult } (Plan 106-11)
 *   DELETE /api/fleet/superadmins/:userId                 → { data: SuperadminMutationResult } (Plan 106-11)
 *
 * The DTOs below mirror the backend field-for-field:
 *   `FleetInstanceView`/`FleetListing`     ← `backend/src/services/fleetConsoleService.ts` (verified zero missing, zero extra — see 106-08-SUMMARY.md)
 *   `ModuleChangePreview`                  ← `backend/src/services/fleetValidationService.ts` (verified zero missing, zero extra — see 106-12-SUMMARY.md)
 *   `SaveOutcome`/`SaveModulesResult`      ← `backend/src/services/fleetSaveService.ts` (verified zero missing, zero extra — see 106-12-SUMMARY.md)
 *   `SuperadminHolder`/`SuperadminMutationResult` ← `backend/src/services/superadminGrantService.ts`
 *
 * This file MUST be updated in the same commit as any change to those
 * backend services, exactly like every other API module in this directory
 * (see `cronJobs.ts`'s header for the same standing rule).
 */
import { apiGet, apiPost, apiDelete } from './client';
import { MODULE_IDS } from './modules';
import type { ModuleId } from './modules';

// Re-exported so components can reuse the existing frontend tuple rather
// than retyping the 13 module ids (see 106-12-PLAN.md's read_first note).
export { MODULE_IDS };
export type { ModuleId };

export interface FleetDriftDetail {
  onlyDesired: string[];
  onlyRunning: string[];
}

export interface FleetInstanceView {
  id: string;
  managed: 'fleet' | 'external';
  wave: string;
  tenancy: string;
  domain: string;
  sentryEnvironment: string;
  ecsFamily: string;
  ecsService: string;
  /** `managed === 'fleet'`. UI hint only — D-08 requires the backend to
   * re-derive this server-side independently for every write endpoint. */
  editable: boolean;
  desiredRaw: string;
  desiredModules: string[];
  desiredAutoEnabled: string[];
  desiredError: string | null;
  runtimeAvailable: boolean;
  runtimeUnavailableReason: string | null;
  runningRaw: string | null;
  runningModules: string[];
  /**
   * A TRUE tri-state: `true` (desired != running), `false` (in sync), or
   * `null` (unknown — the ECS read failed, `runtimeAvailable: false`).
   * NEVER collapse `null` to `false` when rendering this — that would
   * assert "no drift" on absent evidence, which is the exact defect this
   * field's three-state shape exists to prevent (see FleetConsolePage.tsx).
   */
  drift: boolean | null;
  driftDetail: FleetDriftDetail;
  rolloutState: string | null;
  rolloutStateReason: string | null;
  desiredCount: number | null;
  runningCount: number | null;
  taskDefinitionArn: string | null;
  /**
   * Name inventories only — no secret VALUE ever reaches the client. Not
   * rendered on this read-only dashboard at all; retained here solely for
   * Plan 106-12's module editor advisory panel. Do not delete these fields
   * as "unused" — they are load-bearing for that future plan.
   */
  liveEnvNames: string[];
  liveSecretNames: string[];
  /** Phase 108 (Plan 108-06, D-03) — this instance's current per-instance,
   * non-secret env values, keyed by name. Empty object when none are set.
   * Committed to `ops/fleet.yaml` in git — never a secret value. */
  moduleEnv: Record<string, string>;
}

/**
 * Mirrors `ModuleCatalogEntry` in
 * `backend/src/services/fleetConsoleService.ts`. The dependency DAG is
 * served from the backend registry rather than hand-synced into
 * `services/api/modules.ts` alongside `MODULE_IDS`: a stale edge here would
 * make `FleetModuleEditor`'s tree disagree with the closure the server
 * actually resolves, which is exactly the class of bug the hand-synced id
 * tuple gets away with only because a flat list of 13 strings is trivially
 * verifiable by eye.
 */
export interface ModuleCatalogEntry {
  id: ModuleId;
  label: string;
  /** DIRECT dependencies only — the consumer walks the closure. */
  deps: ModuleId[];
}

export interface FleetListing {
  manifestRef: string;
  manifestSha: string;
  baseSha: string;
  region: string;
  cluster: string;
  instances: FleetInstanceView[];
  moduleCatalog: ModuleCatalogEntry[];
}

export async function getFleetInstances(): Promise<FleetListing> {
  const res = await apiGet<{ data: FleetListing }>('/fleet/instances');
  return res.data;
}

// ─────────────────────────────────────────────────────────────────────────
// Plan 106-09 — the hard pre-commit validation/preview gate.
// ─────────────────────────────────────────────────────────────────────────

export interface ModuleChangeAutoEnabled {
  module: string;
  label: string;
  requiredBy: string[];
}

export interface ModuleChangeBlockedDisable {
  module: string;
  label: string;
  blockedBy: string[];
  resolvedSetIfAlsoDisabled: string[];
}

export interface ModuleChangeBlocking {
  rule: string;
  message: string;
  /**
   * Plan 108-07 (D-05/D-06, MODULE-PREREQ-03) — non-null only for a
   * `missing-module-env` reason, sourced verbatim from `docs/private/CONFIG.md`.
   * Rendered as-is, never composed or reworded.
   */
  howToObtain: string | null;
  command: string | null;
}

export interface ModuleChangeInformational {
  rule: string;
  instanceId: string | null;
  message: string;
}

export interface LiveEnvAdvisory {
  available: boolean;
  reason: string | null;
  presentNames: string[];
  absentRequiredNames: string[];
}

/**
 * Mirrors `backend/src/services/fleetValidationService.ts`'s
 * `ModuleChangePreview` field-for-field. `guaranteeNote` is present on
 * EVERY response, allowed or refused — it must be rendered verbatim by the
 * UI (P-47), never paraphrased.
 */
export interface ModuleChangePreview {
  instanceId: string;
  currentRaw: string;
  nextRaw: string;
  requested: string[];
  resolved: string[];
  autoEnabled: ModuleChangeAutoEnabled[];
  blockedDisables: ModuleChangeBlockedDisable[];
  blocking: ModuleChangeBlocking[];
  informational: ModuleChangeInformational[];
  allowed: boolean;
  changed: boolean;
  guaranteeNote: string;
  liveEnvAdvisory: LiveEnvAdvisory | null;
  /** Phase 108 (Plan 108-06, D-03) — the effective moduleEnv map this
   * preview evaluated: the candidate when one was sent, otherwise the
   * instance's own current map. */
  moduleEnv: Record<string, string>;
  /** `true` when the candidate moduleEnv differs from the instance's
   * current map — a moduleEnv-only edit (module set unchanged) must still
   * be able to enable Save. */
  moduleEnvChanged: boolean;
  /** The non-secret module-required env key NAMES the resolved module set
   * needs — what the editor renders an input for. Empty when the resolved
   * set needs none. */
  requiredModuleEnvKeys: string[];
}

/**
 * Read-only preview of a proposed module change. Deliberately a
 * `useMutation` (Plan 106-12), not a `useQuery` — this is a POST carrying a
 * request body and must not be cached across checkbox states.
 */
export async function previewInstanceModules(
  instanceId: string,
  modules: ModuleId[],
  moduleEnv?: Record<string, string>
): Promise<ModuleChangePreview> {
  const res = await apiPost<{ data: ModuleChangePreview }>(
    `/fleet/instances/${instanceId}/preview-modules`,
    { modules, moduleEnv }
  );
  return res.data;
}

// ─────────────────────────────────────────────────────────────────────────
// Plan 106-10 — the write path (commit + trigger).
// ─────────────────────────────────────────────────────────────────────────

/**
 * Mirrors `backend/src/services/fleetSaveService.ts`'s `SaveOutcome`.
 * Deliberately never `'success'` (P-37/P-48) — a module change takes effect
 * only after a ~2-3 minute ECS rolling deploy, and the commit can land while
 * the deploy trigger fails to enqueue.
 */
export type SaveOutcome = 'refused' | 'committed' | 'committed_and_triggered';

/** Mirrors `backend/src/services/fleetSaveService.ts`'s `SaveModulesResult`. */
export interface SaveModulesResult {
  outcome: SaveOutcome;
  preview: ModuleChangePreview;
  commitSha: string | null;
  pipelineNumber: number | null;
  pipelineId: string | null;
  deployReason: string | null;
  /** Operator-facing sentence — render verbatim, never compose a parallel one (P-48). */
  message: string;
}

/**
 * Saves a module change for one fleet instance. `baseSha` must be the one
 * from the listing the operator is looking at, never refetched at save
 * time (P-49) — that is what makes a 409 meaningful.
 */
export async function saveInstanceModules(
  instanceId: string,
  modules: ModuleId[],
  baseSha: string,
  moduleEnv?: Record<string, string>
): Promise<SaveModulesResult> {
  const res = await apiPost<{ data: SaveModulesResult }>(`/fleet/instances/${instanceId}/modules`, {
    modules,
    baseSha,
    moduleEnv,
  });
  return res.data;
}

// ─────────────────────────────────────────────────────────────────────────
// Plan 106-11 — the superadmin grant surface (D-09).
// ─────────────────────────────────────────────────────────────────────────

/** Mirrors `backend/src/services/superadminGrantService.ts`'s `SuperadminListing`. */
export interface SuperadminHolder {
  id: number;
  email: string;
  firstName: string | null;
  lastName: string | null;
  roles: string[];
}

/** Mirrors `backend/src/services/superadminGrantService.ts`'s `SuperadminMutationResult`. */
export interface SuperadminMutationResult {
  id: number;
  email: string;
  isSuperadmin: boolean;
}

export async function listSuperadmins(): Promise<SuperadminHolder[]> {
  const res = await apiGet<{ data: SuperadminHolder[] }>('/fleet/superadmins');
  return res.data;
}

/** Grants by numeric id ONLY — there is deliberately no email variant (P-42). */
export async function grantSuperadmin(userId: number): Promise<SuperadminMutationResult> {
  const res = await apiPost<{ data: SuperadminMutationResult }>('/fleet/superadmins', { userId });
  return res.data;
}

export async function revokeSuperadmin(userId: number): Promise<SuperadminMutationResult> {
  const res = await apiDelete<{ data: SuperadminMutationResult }>(`/fleet/superadmins/${userId}`);
  return res.data;
}
